import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  GithubOutlined,
  SettingOutlined,
  TagsOutlined,
} from '@ant-design/icons';
import { Modal, Spin } from 'antd';
import Workspace, {
  WorkspaceDefaultModule,
  WorkspaceEstimationType,
  WorkspaceFramework,
} from '../../../types/Workspace';
import { UpdateSpaceSettingsModel } from '../../../api/workspace';
import { useEffect, useState } from 'react';
import { WORKSPACE_VISIBILITY } from '../../Onboarding/WorkspaceOnboarding';
import './SpaceSettingsModal.scss';
import { DisplayLayout } from '../../../assets/CustomIcons/DisplayLayout';
import { SpaceSettingsFramework } from './components/SpaceSettingsFramework';
import { SpaceSettingsGeneral } from './components/SpaceSettingsGeneral';
import { SpaceSettingsProperties } from './components/SpaceSettingsProperties';
import { SpaceSettingsImportTasks } from './components/SpaceSettingsImportTasks';
import { SpaceSettingsGithub } from './components/SpaceSettingsGithub';
import { MigrationStatusMapping } from './components/SpaceTasksMigration';
import SettingsSideBarItem from '../../../components/UI/Components/SettingsSideBarItem';
import { SprintIcon } from '../../../assets/CustomIcons/SprintIcon';
import { ImportOutlined } from '@ant-design/icons';
import { WorkspaceAccessRole } from '../../../types/WorkspaceAccess';
import { BaseButton } from '../../../components/UI/BaseButton';
import { EditStatusesModalContent } from '../../../components/Modal/EditStatusesModalContent';
import { EditLabelsModalContent } from '../../../components/Modal/EditLabelsModalContent';

export type SpaceSettingsModalProps = {
  isOpen: boolean;
  onCancel: () => void;
  space?: Workspace;
  userWorkspaceRole?: WorkspaceAccessRole;
  selectedTab: SpaceSettingsTab;
  setSelectedTab: (tab: SpaceSettingsTab) => void;
  handleConfirmSaveUpdate: (updateModel: UpdateSpaceSettingsModel) => void;
  handleLeaveSpace: () => void;
  handleDeleteSpace: () => void;
};

export enum SpaceSettingsTab {
  GENERAL = 'GENERAL',
  FRAMEWORK = 'FRAMEWORK',
  PROPERTIES = 'PROPERTIES',
  STATUSES = 'STATUSES',
  LABELS = 'LABELS',
  INTEGRATIONS = 'INTEGRATIONS',
  IMPORT_TASKS = 'IMPORT_TASKS',
}

export const SpaceSettingsModal = ({
  isOpen,
  onCancel,
  space,
  userWorkspaceRole,
  selectedTab,
  setSelectedTab,
  handleConfirmSaveUpdate,
  handleLeaveSpace,
  handleDeleteSpace,
}: SpaceSettingsModalProps) => {
  const [tabToGoAfterUnsavedModal, setTabToGoAfterUnsavedModal] = useState<SpaceSettingsTab>(SpaceSettingsTab.GENERAL);
  const [isChanged, setIsChanged] = useState<boolean>(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState<boolean>(false);
  const [rerender, setRerender] = useState<boolean>(false);

  const isLoading = userWorkspaceRole === undefined;
  const isDisabled = !isLoading && userWorkspaceRole !== WorkspaceAccessRole.Owner;

  const onClose = async () => {
    if (isChanged) {
      setShowUnsavedChangesModal(true);
      setTabToGoAfterUnsavedModal(selectedTab);
      return;
    }
    onCancel();
  };

  const onTabChange = (tab: SpaceSettingsTab) => {
    if (isChanged && tab !== SpaceSettingsTab.IMPORT_TASKS) {
      setShowUnsavedChangesModal(true);
      setTabToGoAfterUnsavedModal(tab);
      return;
    }
    setSelectedTab(tab);
  };

  const saveSpaceGeneralSettings = async (spaceName: string, visibility: WORKSPACE_VISIBILITY) => {
    const updateModel: UpdateSpaceSettingsModel = {
      name: spaceName,
      visibility: visibility,
    };
    handleConfirmSaveUpdate(updateModel);
    setIsChanged(false);
  };

  const saveSpaceFrameworkSettings = async (
    framework: WorkspaceFramework,
    statusesMapping: MigrationStatusMapping,
    defaultModule?: WorkspaceDefaultModule
  ) => {
    const updateModel: UpdateSpaceSettingsModel = {
      framework: framework,
      defaultModule: defaultModule,
      statusesMapping: framework != space?.framework ? statusesMapping : undefined,
    };
    handleConfirmSaveUpdate(updateModel);
    setIsChanged(false);
  };

  const saveSpacePropertiesSettings = async (estimationType: WorkspaceEstimationType) => {
    const updateModel: UpdateSpaceSettingsModel = {
      estimationType: estimationType,
    };
    handleConfirmSaveUpdate(updateModel);
    setIsChanged(false);
  };

  useEffect(() => {
    if (isOpen) {
      setRerender(true);
    } else {
      setSelectedTab(SpaceSettingsTab.GENERAL);
    }
  }, [isOpen]);

  return (
    <Modal title={null} width={888} open={isOpen} onCancel={onClose} footer={null} centered className="custom-modal">
      <div className="flex">
        <div className="w-[248px] min-h-[556px] self-stretch bg-neutral-200 flex flex-col items-center justify-between rounded">
          <div className="w-[calc(100%-16px)] flex items-center justify-center bg-white rounded py-3 mt-3">
            <div className="w-[calc(100%-24px)] flex flex-col gap-1 items-center">
              <SettingsSideBarItem
                iconLeft={<SettingOutlined />}
                text={'General'}
                selected={selectedTab === SpaceSettingsTab.GENERAL}
                onClick={() => onTabChange(SpaceSettingsTab.GENERAL)}
              />
              <SettingsSideBarItem
                iconLeft={<SprintIcon />}
                text={'Framework'}
                selected={selectedTab === SpaceSettingsTab.FRAMEWORK}
                onClick={() => onTabChange(SpaceSettingsTab.FRAMEWORK)}
              />

              <SettingsSideBarItem
                iconLeft={<DisplayLayout />}
                text={'Properties'}
                selected={selectedTab === SpaceSettingsTab.PROPERTIES}
                onClick={() => onTabChange(SpaceSettingsTab.PROPERTIES)}
              />

              <SettingsSideBarItem
                iconLeft={<CheckCircleOutlined />}
                text={'Statuses'}
                selected={selectedTab === SpaceSettingsTab.STATUSES}
                onClick={() => onTabChange(SpaceSettingsTab.STATUSES)}
              />

              <SettingsSideBarItem
                iconLeft={<TagsOutlined />}
                text={'Labels'}
                selected={selectedTab === SpaceSettingsTab.LABELS}
                onClick={() => onTabChange(SpaceSettingsTab.LABELS)}
              />

              <SettingsSideBarItem
                iconLeft={<GithubOutlined />}
                text={'GitHub integration'}
                selected={selectedTab === SpaceSettingsTab.INTEGRATIONS}
                onClick={() => onTabChange(SpaceSettingsTab.INTEGRATIONS)}
              />

              {import.meta.env.VITE_FEATURE_IMPORT === 'true' && (
                <SettingsSideBarItem
                  iconLeft={<ImportOutlined />}
                  text={'Import Tasks'}
                  selected={selectedTab === SpaceSettingsTab.IMPORT_TASKS}
                  onClick={() => onTabChange(SpaceSettingsTab.IMPORT_TASKS)}
                />
              )}
            </div>
          </div>

          <div className="w-[calc(100%-16px)] flex items-center justify-center bg-white rounded py-3 mb-3">
            <div className="w-[calc(100%-24px)] flex flex-col gap-1 items-center">
              <SettingsSideBarItem
                iconLeft={<ArrowLeftOutlined />}
                text={'Leave Space'}
                selected={false}
                onClick={() => handleLeaveSpace()}
              />
              {userWorkspaceRole === WorkspaceAccessRole.Owner && (
                <SettingsSideBarItem
                  iconLeft={<DeleteOutlined />}
                  text={'Delete Space'}
                  selected={false}
                  onClick={() => handleDeleteSpace()}
                  danger
                />
              )}
            </div>
          </div>
        </div>
        <div className="w-[640px] flex items-center">
          {isLoading ? (
            <div className="w-full flex items-center justify-center py-20">
              <Spin />
            </div>
          ) : (
            <>
              {selectedTab === SpaceSettingsTab.GENERAL && (
                <SpaceSettingsGeneral
                  space={space}
                  onSave={saveSpaceGeneralSettings}
                  setIsChanged={setIsChanged}
                  isDisabled={isDisabled}
                  rerender={rerender}
                  setRerender={setRerender}
                />
              )}
              {selectedTab === SpaceSettingsTab.FRAMEWORK && (
                <SpaceSettingsFramework
                  space={space}
                  onSave={saveSpaceFrameworkSettings}
                  setIsChanged={setIsChanged}
                  isDisabled={isDisabled}
                />
              )}
              {selectedTab === SpaceSettingsTab.PROPERTIES && (
                <SpaceSettingsProperties
                  space={space}
                  onSave={saveSpacePropertiesSettings}
                  setIsChanged={setIsChanged}
                  isDisabled={isDisabled}
                />
              )}
              {selectedTab === SpaceSettingsTab.STATUSES && (
                <div className="w-full h-full px-8 pt-6 flex flex-col items-start">
                  <div className="text-brand-grey-dark text-base font-semibold mb-4">Manage Statuses</div>
                  <div className="w-full">
                    <EditStatusesModalContent workspaceId={space?.id} />
                  </div>
                </div>
              )}
              {selectedTab === SpaceSettingsTab.LABELS && (
                <div className="w-full h-full px-8 pt-6 flex flex-col items-start">
                  <div className="text-brand-grey-dark text-base font-semibold mb-4">Manage Labels</div>
                  <div className="w-full">
                    <EditLabelsModalContent />
                  </div>
                </div>
              )}
              {selectedTab === SpaceSettingsTab.INTEGRATIONS && (
                <SpaceSettingsGithub space={space} setIsChanged={setIsChanged} isDisabled={isDisabled} />
              )}
              {selectedTab === SpaceSettingsTab.IMPORT_TASKS && (
                <SpaceSettingsImportTasks workspaceId={space?.id} onCloseModal={onCancel} />
              )}
            </>
          )}
        </div>
      </div>

      <Modal
        title={<div className="text-brand-grey-dark text-base font-semibold">You have some unsaved changes</div>}
        open={showUnsavedChangesModal}
        onOk={() => setShowUnsavedChangesModal(false)}
        onCancel={() => setShowUnsavedChangesModal(false)}
        centered
        footer={
          <div className="flex items-center justify-between">
            <BaseButton
              type="secondary"
              size="medium"
              danger
              onClick={() => {
                setShowUnsavedChangesModal(false);
                if (tabToGoAfterUnsavedModal !== selectedTab) {
                  setIsChanged(false);
                  setSelectedTab(tabToGoAfterUnsavedModal);
                } else {
                  setIsChanged(false);
                  setTimeout(() => {
                    onCancel();
                  }, 50);
                }
              }}
            >
              Dismiss changes
            </BaseButton>
            <BaseButton type="secondary" size="medium" onClick={() => setShowUnsavedChangesModal(false)}>
              Back to editing
            </BaseButton>
          </div>
        }
      >
        <div className="text-brand-grey-dark text-sm font-normal mt-6 mb-10">
          Leaving this page now will result with your changes not being applied.
        </div>
      </Modal>
    </Modal>
  );
};
